/*
 * DD AdminUI
 * Copyright (C) Fraunhofer IESE 2019 - 2025 Stefan Schweitzer, Shyalan Ramesh, Rafael Aranda López King,
 * Florian Wege, Johannes Schneider, Kazi Rezoanur Rahman, Christoph Müller, Pedram Jokar,
 * Mher Ter-Tovmasyan, Ben Burkhard, Matthias Gerbershagen, Anna Kleiner, Muhammad Tariq,
 * Adeline Silva Schäfer
 *
 * SPDX-License-Identifier: Apache-2.0
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { ThunkAction } from 'redux-thunk';
import {
	GET_PUSH_EVENTS_FAILURE,
	GET_PUSH_EVENTS_REQUEST,
	GET_PUSH_EVENTS_SUCCESS,
	GetPushEventsFailureAction,
	GetPushEventsRequestAction,
	GetPushEventsSuccessAction,
	PushEventActionTypes
} from './PushEventActionTypes';
import { BaseError } from '../../../shared/errors/Errors';
import { wrapIntoErrorObject } from '../../../shared/errors/ErrorWrapper';
import { PlatformAdminUIState, PlatformAdminUIThunkType } from '../../../store/store';
import { sharedAdminuiPushApi } from '../../../ServiceFactory';
import { ListPushEvent } from './PushEvent.state';
import { ClientPushEventDocumentation } from '@DigitaleDoerfer/digitale-doerfer-api';

export type ThunkResult<R> = ThunkAction<R, PlatformAdminUIState, undefined, PushEventActionTypes>;

export const getPushEventsRequest = (): GetPushEventsRequestAction => {
	return {
		type: GET_PUSH_EVENTS_REQUEST
	};
};

export const getPushEventsSuccess = (pushEvents: ListPushEvent[]): GetPushEventsSuccessAction => {
	return {
		type: GET_PUSH_EVENTS_SUCCESS,
		pushEvents
	};
};

export const getPushEventsFailure = (error: BaseError): GetPushEventsFailureAction => {
	return {
		type: GET_PUSH_EVENTS_FAILURE,
		error
	};
};

export const getPushEvents = (): ThunkResult<Promise<void>> => {
	return async (
		dispatch: PlatformAdminUIThunkType<PushEventActionTypes>,
		getState: () => PlatformAdminUIState
	): Promise<void> => {
		try {
			dispatch(getPushEventsRequest());

			const response = await sharedAdminuiPushApi().getPushEventOverviewJson({});

			// Prepate data to dispatch getPushedEventsSuccess
			const pushEvents = response
				? response.map((pushEvent: ClientPushEventDocumentation, index: number) => ({ ...pushEvent, id: index }))
				: [];
			dispatch(getPushEventsSuccess(pushEvents));
		} catch (error) {
			dispatch(getPushEventsFailure(wrapIntoErrorObject(error)));
		}
	};
};
